import { type DragEndEvent, type UseDroppableInput } from "@z/dnd";
import { movePane } from "../model/ops";
import { findPane } from "../model/tree";
import type { LayoutTree } from "../model/types";
import type { Edge } from "../model/types";

/** @deprecated Tabs now use dnd-kit instead of HTML dataTransfer. */
export const LAYOUT_DRAG_MIME = "data-layout-drag";

export type DropZone = Edge | "center";

/**
 * Which drop zone of `rect` the point is in: the outer `fraction` on each side docks as a split,
 * the middle moves the tab into the group.
 */
export function zoneAt(rect: DOMRect, x: number, y: number, fraction = 0.25): DropZone {
  if (rect.width <= 0 || rect.height <= 0) return "center";
  const fx = (x - rect.left) / rect.width;
  const fy = (y - rect.top) / rect.height;
  const dl = fx < fraction ? x - rect.left : Infinity;
  const dr = fx > 1 - fraction ? rect.right - x : Infinity;
  const dt = fy < fraction ? y - rect.top : Infinity;
  const db = fy > 1 - fraction ? rect.bottom - y : Infinity;
  const min = Math.min(dl, dr, dt, db);
  if (!Number.isFinite(min)) return "center";
  if (min === dl) return "left";
  if (min === dr) return "right";
  if (min === dt) return "top";
  return "bottom";
}

/** Pointer position, not the width of the dragged tab, selects a dock. Overlapping corners
 * prefer the physically closest edge. Keyboard targets keep their ordinary rectangular shape. */
export const dockCollision: NonNullable<UseDroppableInput["collisionDetector"]> = ({
  droppable,
  dragOperation,
}) => {
  const rect = droppable.shape?.boundingRectangle;
  if (!rect) return null;
  const { x, y } = dragOperation.position.current;
  if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) return null;
  const edge = droppable.data["edge"];
  const distance =
    edge === "left"
      ? x - rect.left
      : edge === "right"
        ? rect.right - x
        : edge === "top"
          ? y - rect.top
          : edge === "bottom"
            ? rect.bottom - y
            : 0;
  return {
    id: droppable.id,
    type: 2,
    priority: droppable.data["workspace"] ? 10 : edge && edge !== "center" ? 1 : 0,
    value: 10000 - distance,
  };
};

export const tabCollision: NonNullable<UseDroppableInput["collisionDetector"]> = (input) => {
  if (input.dragOperation.activatorEvent instanceof KeyboardEvent && input.droppable.data["after"])
    return null;
  return dockCollision(input);
};

/** Commit a tab insertion, a pane split, or a workspace split. */
export function paneDrop(tree: LayoutTree, event: DragEndEvent): LayoutTree {
  const { source, target } = event.operation;
  if (event.canceled || !source || !target) return tree;
  const edge = target.data["edge"];
  if (
    target.data["workspace"] &&
    (edge === "left" || edge === "right" || edge === "top" || edge === "bottom")
  )
    return movePane(tree, String(source.id), { workspace: true, edge });
  const groupId = target.data["groupId"];
  if (typeof groupId !== "string") return tree;
  if (edge === "left" || edge === "right" || edge === "top" || edge === "bottom") {
    return movePane(tree, String(source.id), { groupId, edge });
  }
  const slot = target.data["index"];
  if (typeof slot === "number") {
    const found = findPane(tree, String(source.id));
    const from = found?.group.tabs.findIndex((tab) => tab.id === source.id) ?? -1;
    const index =
      slot +
      (event.operation.activatorEvent instanceof KeyboardEvent &&
      found?.group.id === groupId &&
      slot > from
        ? 1
        : 0);
    return movePane(tree, String(source.id), { groupId, index });
  }
  return movePane(tree, String(source.id), { groupId });
}
